import React, { useEffect, useState } from 'react';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import {
  getFilteredUsersFromUserGroup,
  isPublicPage,
  sortLastNamesDesc,
} from '../../scripts/utils';
import Add from '@material-ui/icons/Add';
import CircularLoader from '../loader/CircularLoader';
import IconButton from '@material-ui/core/IconButton';
import { IDocumentUser, IDocumentUserGroup } from '../../api-client/autogenerated';
import { useDispatch, useSelector } from 'react-redux';
import { getDocumentState } from '../../features/document/selectors';
import { modifyDocumentById } from '../../models/api/documents';
import { reloadDocument, setDocument } from '../../features/document/actions';
import { Remove } from '@material-ui/icons';
import { Collapse, Table, TableBody, useMediaQuery, useTheme } from '@material-ui/core';
import ManagePermissionsDialog, {
  ManagePermissionsDialogType,
  PermissionActionType,
  PermissionsAndAction,
} from '../design/ManagePermissionsDialog';
import { SmallButton } from '../custom-components/CustomButtons';
import { getDocPermission, userIsOnProject } from '../../scripts/store-utils';
import PermissionsDialogRow from '../design/PermissionsDialogRow';
import { ImmutableUserType, PermissionTagType } from '../design/PermissionTag';
import { ascendingComparator } from '../document-index/DocumentIndexUtils';
import Button from '@material-ui/core/Button';
import { getUserState } from '../../features/user/selectors';
import { getCurrentProjectUser } from '../../features/project/selectors';

type Props = {
  title?: string;
};

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      width: '100%',
      display: 'inline-flex',
      flexDirection: 'column',
      flexBasis: 'auto',
      zIndex: -1,
    },
    content: {
      display: 'flex',
      // flexDirection: 'column',
      flexBasis: 'auto',
      width: 'auto',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    padding: {
      paddingLeft: 16,
      paddingRight: 16,
      paddingTop: 14,
      paddingBottom: 14,
    },
    centerIcon: {
      display: 'flex',
      alignItems: 'center',
      flexBasis: 'auto',
      flexWrap: 'nowrap',
      height: '24px',
      justifyContent: 'flex-end',
    },
    icon: {
      color: '#7A797A',
      cursor: 'pointer',
    },
  }),
);

export default function Associations(props: Props) {
  const classes = useStyles();
  const { title } = props;

  const theme = useTheme();
  const dispatch = useDispatch();
  const user = useSelector(getUserState);
  const projectUser = useSelector(getCurrentProjectUser);
  const document = useSelector(getDocumentState);

  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const [collapseOpen, setCollapseOpen] = useState(true);
  const [addFollowerDialogOpen, setOpenAddFollowerDialog] = useState(false);
  const [loadingNotificationList, setLoadingNotificationList] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [associatedGroups, setAssociatedGroups] = useState<IDocumentUserGroup[] | undefined>([]);
  const [associatedUsers, setAssociatedUsers] = useState<IDocumentUser[] | undefined>([]);

  const isCurrentUserOnNotificationList = associatedUsers?.some((u) => u.userId === user.id);

  const documentArchitect = document?.architectUser || null;
  const documentContractor = document?.generalContractorUser || null;
  const documentCreator =
    document?.creatorUser &&
    document.creatorUserId !== document.architectUserId &&
    document.creatorUserId !== document.generalContractorUserId
      ? document.creatorUser
      : null;

  useEffect(() => {
    setAssociatedGroups(document?.documentUserGroupList);

    const relevantDocumentUserIds: string[] = [];
    if (document?.generalContractorUserId)
      relevantDocumentUserIds.push(document.generalContractorUserId);
    if (document?.architectUserId) relevantDocumentUserIds.push(document.architectUserId);
    if (document?.creatorUserId) relevantDocumentUserIds.push(document.creatorUserId);

    setAssociatedUsers(
      document?.documentUserList?.filter((du) => !relevantDocumentUserIds.includes(du.userId)),
    );
  }, [document]);

  const handleAddFollowerDialogOpen = () => {
    setOpenAddFollowerDialog(true);
  };

  const handleAddFollowerDialogClose = () => {
    setOpenAddFollowerDialog(false);
  };

  const handleDeletePermission = (permission: PermissionsAndAction) => {
    if (permission.group && document && document.documentUserGroupList) {
      const documentTemp = { ...document };
      const index = documentTemp.documentUserGroupList!.findIndex(
        (x) => x.userGroupId === permission.group?.id,
      );
      documentTemp.documentUserGroupList!.splice(index, 1);
      dispatch(setDocument(documentTemp));
      void modifyDocumentById(document.id, {
        followers: { removeUserGroupIds: [permission.group.id] },
      });
    } else if (permission.userId && document && document.documentUserList) {
      const documentTemp = { ...document };
      const index = documentTemp.documentUserList!.findIndex((x) => x.userId === permission.userId);
      documentTemp.documentUserList!.splice(index, 1);
      dispatch(setDocument(documentTemp));
      void modifyDocumentById(document.id, { followers: { removeUserIds: [permission.userId] } });
    }
  };

  const addNewUsersToNotificationList = async (
    userIds: string[],
    associatedGroupIds: string[],
    associatedDocumentIds: string[],
  ) => {
    try {
      setLoadingNotificationList(true);

      const addGroupIds = associatedGroupIds.filter(
        (id) => !document!.documentUserGroupList!.some(({ userGroupId }) => userGroupId === id),
      );
      const removeGroupIds = document!
        .documentUserGroupList!.filter(
          ({ userGroupId }) => !associatedGroupIds.some((id) => id === userGroupId),
        )
        .map(({ userGroupId }) => userGroupId);

      const removeUserIds = document!
        .documentUserList!.filter(({ userId }) => !userIds.some((id) => id === userId))
        .map(({ userId }) => userId);

      await modifyDocumentById(document!.id, {
        followers: {
          addUserIds: userIds,
          removeUserIds,
          addUserGroupIds: addGroupIds,
          removeUserGroupIds: removeGroupIds,
        },
      });

      dispatch(reloadDocument());
    } catch (e: any) {
      console.log(e);
    } finally {
      setLoadingNotificationList(false);
    }
  };

  const hasEditPermission = () => {
    if (document?.isHidden) return false;
    const permission = getDocPermission();
    return permission && permission >= 2;
  };

  const addCurrentUserToNotificationList = async () => {
    if (!document) return;

    try {
      setIsSubmitting(true);
      await modifyDocumentById(document.id, { followers: { addUserIds: [user.id] } });

      dispatch(reloadDocument());
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className={classes.root}>
      <CardContent className={classes.content}>
        <h2
          style={{
            marginBottom: 10,
            lineHeight: '28px',
            marginRight: 32,
          }}
          className="h2"
        >
          {title
            ? `${title} Notification List`
            : `${document?.referenceNumber}${
                document?.revisionNumber ? `-R${document?.revisionNumber}` : ''
              } Notification List`}
        </h2>

        <div style={{ display: 'flex', flexWrap: 'wrap', rowGap: 8 }}>
          {!isPublicPage() && hasEditPermission() ? (
            <SmallButton
              variant="outlined"
              color="primary"
              style={{ marginRight: 16, width: 100 }}
              onClick={handleAddFollowerDialogOpen}
            >
              Edit
            </SmallButton>
          ) : null}
          {!isPublicPage() &&
          !isCurrentUserOnNotificationList &&
          userIsOnProject(projectUser, user) ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={
                isSubmitting ? <CircularLoader size={20} loaderStyle={{ color: 'white' }} /> : null
              }
              onClick={addCurrentUserToNotificationList}
              style={{ whiteSpace: 'nowrap', width: isSmall ? 100 : 'auto' }}
            >
              {isSmall ? 'Add Me' : 'Add Me to the Notification List'}
            </Button>
          ) : null}
        </div>
        <div style={{ display: 'inline-flex', flexGrow: 100 }} />
        <IconButton style={{ padding: 0 }}>
          {collapseOpen ? (
            <Remove htmlColor="#0947B9" onClick={() => setCollapseOpen(false)} />
          ) : (
            <Add htmlColor="#0947B9" onClick={() => setCollapseOpen(true)} />
          )}
        </IconButton>
      </CardContent>
      <Collapse in={collapseOpen}>
        {loadingNotificationList ? (
          <CircularLoader />
        ) : (
          <Table size="small" padding="none" stickyHeader>
            <TableBody>
              {associatedGroups
                ?.sort((a, b) => ascendingComparator(a, b, 'userGroup', true))
                .map((group, index) => (
                  <PermissionsDialogRow
                    key={group.id}
                    permissionRow={{
                      action: PermissionActionType.None,
                      read: true,
                      write: true,
                      admin: true,
                      group: group.userGroup!,
                    }}
                    index={index}
                    users={getFilteredUsersFromUserGroup(
                      ManagePermissionsDialogType.AssociatedUsers,
                      group.userGroup!,
                    )}
                    type={PermissionTagType.Group}
                    label={group.userGroup?.name || ''}
                    defaultOpen
                    hidePermissions
                    dialogType={ManagePermissionsDialogType.AssociatedUsers}
                    handleEditPermission={() => {}}
                    handleDeletePermission={handleDeletePermission}
                    isDocumentDetail
                  />
                ))}
              {associatedUsers
                ?.sort((a, b) => sortLastNamesDesc(a.user?.name, b.user?.name))
                .map((user, index) => (
                  <PermissionsDialogRow
                    key={user.id}
                    permissionRow={{
                      action: PermissionActionType.None,
                      read: true,
                      write: true,
                      admin: true,
                      userId: user.user?.id,
                    }}
                    index={index}
                    users={[]}
                    type={PermissionTagType.Person}
                    label={user.user?.name || ''}
                    defaultOpen
                    hidePermissions
                    dialogType={ManagePermissionsDialogType.AssociatedUsers}
                    handleEditPermission={() => {}}
                    handleDeletePermission={handleDeletePermission}
                    isDocumentDetail
                    documentUser={user}
                  />
                ))}
              {documentCreator ? (
                <PermissionsDialogRow
                  key={documentCreator.id + 'creator'}
                  permissionRow={{
                    action: PermissionActionType.None,
                    read: true,
                    write: true,
                    admin: true,
                    userId: documentCreator.id,
                  }}
                  index={(associatedUsers?.length || 0) + 1}
                  users={[]}
                  type={PermissionTagType.Person}
                  label={documentCreator.name}
                  defaultOpen
                  hidePermissions
                  dialogType={ManagePermissionsDialogType.AssociatedUsers}
                  handleEditPermission={() => {}}
                  isDocumentDetail
                  disableDelete={ImmutableUserType.Creator}
                />
              ) : null}
              {documentArchitect ? (
                <PermissionsDialogRow
                  key={documentArchitect.id + 'architect'}
                  permissionRow={{
                    action: PermissionActionType.None,
                    read: true,
                    write: true,
                    admin: true,
                    userId: documentArchitect.id,
                  }}
                  index={(associatedUsers?.length || 0) + 1}
                  users={[]}
                  type={PermissionTagType.Person}
                  label={documentArchitect.name}
                  defaultOpen
                  hidePermissions
                  dialogType={ManagePermissionsDialogType.AssociatedUsers}
                  handleEditPermission={() => {}}
                  isDocumentDetail
                  disableDelete={ImmutableUserType.Architect}
                />
              ) : null}
              {documentContractor ? (
                <PermissionsDialogRow
                  key={documentContractor.id + 'contractor'}
                  permissionRow={{
                    action: PermissionActionType.None,
                    read: true,
                    write: true,
                    admin: true,
                    userId: documentContractor.id,
                  }}
                  index={(associatedUsers?.length || 0) + (documentArchitect ? 2 : 1)}
                  users={[]}
                  type={PermissionTagType.Person}
                  label={documentContractor.name}
                  defaultOpen
                  hidePermissions
                  dialogType={ManagePermissionsDialogType.AssociatedUsers}
                  handleEditPermission={() => {}}
                  isDocumentDetail
                  disableDelete={ImmutableUserType.Contractor}
                />
              ) : null}
            </TableBody>
          </Table>
        )}
      </Collapse>
      <ManagePermissionsDialog
        dialogOpen={addFollowerDialogOpen}
        closeDialog={handleAddFollowerDialogClose}
        type={ManagePermissionsDialogType.AssociatedUsers}
        associatedUsers={
          document?.documentUserList ? document.documentUserList.map((x) => x.user!.id) : []
        }
        associatedGroups={
          document?.documentUserGroupList?.map(({ userGroupId }) => userGroupId) || []
        }
        submitAssociatedUsersAndGroups={addNewUsersToNotificationList}
      />
    </Card>
  );
}
